import { useState } from "react";
import { Archive, ChevronsDownUp, FilePlus2, FolderPlus, Trash2 } from "lucide-react";
import { IconButton } from "@/ui/IconButton";
import { Tree } from "@/views/tree/Tree";
import { createNote, createFolder, trashItem, archiveItem } from "@/commands/nav";
import { useExpanded } from "@/store/expanded";
import { useRenameRequest } from "@/store/renameRequest";
import { getData, useDataContextId, useWorkspaceItems } from "@/data/store";
import { useDocPresence } from "@/data/presence";
import { PresenceChips } from "@/ui/PresenceChips";
import { docName } from "@hydra-notes/shared";
import { useTrashDialog } from "@/components/TrashDialog";
import { useArchiveDialog } from "@/components/ArchiveDialog";
import s from "./panels.module.css";
import t from "./TreePanel.module.css";

function draggedItemId(dataTransfer: DataTransfer): string | null {
  const raw = dataTransfer.getData("application/x-hydra-item");
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as { id?: unknown };
    return typeof parsed.id === "string" ? parsed.id : null;
  } catch {
    // Older tabs used the bare item id.
    return raw;
  }
}

export function TreePanel() {
  const collapseAll = useExpanded((e) => e.collapseAll);
  const items = useWorkspaceItems();
  const spaceId = useDataContextId();
  const present = useDocPresence(spaceId, getData().sync ? docName.workspace(spaceId) : undefined);
  const openTrash = useTrashDialog((d) => d.setOpen);
  const openArchive = useArchiveDialog((d) => d.setOpen);
  const [trashHover, setTrashHover] = useState(false);
  const [archiveHover, setArchiveHover] = useState(false);
  // Cards and task lists expose their own Archive and Trash surfaces.
  const isNoteItem = (type: string) => type !== "card" && type !== "cardboard" && type !== "tasklist";
  const trashCount = items.filter((i) => isNoteItem(i.type) && i.trashedAt).length;
  const archiveCount = items.filter((i) => isNoteItem(i.type) && i.archivedAt && !i.trashedAt).length;
  return (
    <>
      <div className={s.header}>
        <span className={s.headerTitle}>Notes</span>
        <PresenceChips present={present} compact />
        <IconButton label="New note" kbd="Mod+N" onClick={() => createNote(null)}>
          <FilePlus2 size={14} />
        </IconButton>
        <IconButton
          label="New folder"
          kbd="Mod+Shift+N"
          onClick={() => {
            const id = createFolder(null);
            if (id) useRenameRequest.getState().request(id);
          }}
        >
          <FolderPlus size={14} />
        </IconButton>
        <IconButton label="Collapse all" onClick={collapseAll}>
          <ChevronsDownUp size={14} />
        </IconButton>
      </div>
      <Tree />
      <button
        type="button"
        className={[t.trashRow, archiveHover ? t.archiveHover : ""].join(" ")}
        onClick={() => openArchive(true)}
        onDragOver={(e) => {
          if (e.dataTransfer.types.includes("application/x-hydra-item")) {
            e.preventDefault();
            setArchiveHover(true);
          }
        }}
        onDragLeave={() => setArchiveHover(false)}
        onDrop={(e) => {
          e.preventDefault();
          setArchiveHover(false);
          const id = draggedItemId(e.dataTransfer);
          if (id) archiveItem(id);
        }}
      >
        <Archive size={13} />
        Archive{archiveCount > 0 ? ` (${archiveCount})` : ""}
      </button>
      <button
        type="button"
        className={[t.trashRow, trashHover ? t.trashHover : ""].join(" ")}
        onClick={() => openTrash(true)}
        onDragOver={(e) => {
          if (e.dataTransfer.types.includes("application/x-hydra-item")) {
            e.preventDefault();
            setTrashHover(true);
          }
        }}
        onDragLeave={() => setTrashHover(false)}
        onDrop={(e) => {
          e.preventDefault();
          setTrashHover(false);
          const id = draggedItemId(e.dataTransfer);
          if (id) trashItem(id);
        }}
      >
        <Trash2 size={13} />
        Trash{trashCount > 0 ? ` (${trashCount})` : ""}
      </button>
    </>
  );
}
